import { z } from 'zod';
import { assetKindSchema } from './enums.js';
/**
 * Contrats des PARTAGES PUBLICS ANONYMES (XPHOTO-31, ADR-0009) — source unique
 * front + back.
 *
 * Un partage = jeton opaque, révocable, à durée de vie réglable, qui donne accès EN
 * LECTURE à un ensemble d'assets **sans compte et sans session**. C'est la différence
 * de fond avec un LIEN D'ACCÈS (`session-link.ts`), qui ouvre une vraie session sur un
 * compte : ici rien n'est authentifié, un jeton résout vers un ensemble d'assets.
 *
 * Le jeton BRUT (32 octets base64url) n'est jamais persisté — la base ne stocke que son
 * SHA-256 — et n'est renvoyé QU'UNE SEULE FOIS, à la création (`shareCreatedSchema.secret`).
 *
 * ── DEUX FAMILLES DISJOINTES ───────────────────────────────────────────────────────
 *
 * - `public*` : ce que reçoit un INCONNU. Volontairement minuscule (cf. ci-dessous).
 * - le reste  : ce que manipule un compte connecté (création, listes, gouvernance).
 *
 * Aucune route ne sert les deux. Un champ ajouté à la galerie n'atteint donc jamais la
 * surface publique par accident : il faudrait l'ajouter ICI, explicitement.
 *
 * ── AUCUN `.transform` ─────────────────────────────────────────────────────────────
 *
 * Les schémas de CORPS/QUERY de route n'en contiennent aucun (piège Zod v4 :
 * `z.toJSONSchema` lève AU BOOT → API en boucle de redémarrage, non détecté par le
 * gate de tests ; cf. `route-json-schema.test.ts`). Conséquence assumée : `expiresIn`
 * est une DURÉE SYMBOLIQUE, convertie en date absolue par le handler, et la cohérence
 * `kind` ↔ (`folderPath` | `assetIds`) est vérifiée par le handler elle aussi.
 */
/** Longueur maximale du titre d'un partage (garde-fou contrat). */
export const SHARE_TITLE_MAX = 120;
/**
 * Plafond d'identifiants acceptés par une SÉLECTION figée. Garde-fou : au-delà, on
 * partage un dossier, pas une sélection — et un corps de requête n'a pas à porter des
 * dizaines de milliers d'uuid.
 */
export const SHARE_ASSET_IDS_MAX = 2000;
/**
 * Forme du périmètre partagé. DEUX seulement, et c'est un choix (ADR-0009) :
 * - `folder`    : un préfixe de `rel_path`, RÉCURSIF (`''` = toute la bibliothèque) ;
 * - `selection` : une liste d'ids FIGÉE (une photo seule = une liste de un).
 *
 * Les vues FILTRÉES sont volontairement absentes : un filtre est réévalué à chaque
 * visite, donc des photos futures entreraient dans un lien public déjà diffusé.
 */
export const shareKindSchema = z.enum(['folder', 'selection']);
/**
 * Durée de vie DEMANDÉE à la création. Symbolique et non une date : le client n'a pas
 * à calculer une échéance (et son horloge n'est pas digne de confiance). Le handler la
 * convertit en `expires_at` absolu ; `never` donne `NULL` (éternel).
 */
export const shareExpirySchema = z.enum(['7d', '30d', '1y', 'never']);
// ───────────────────────────────────────────────────────────────────────────────────
// SURFACE PUBLIQUE — ce que reçoit un visiteur sans compte.
// ───────────────────────────────────────────────────────────────────────────────────
/**
 * Un média tel que le voit un INCONNU. Trois champs de données, pas un de plus.
 *
 * Ce qui est ABSENT l'est délibérément : ni `relPath` (révélerait l'arborescence, donc
 * des noms de personnes ou d'événements), ni `filename`, ni `identityHash`, ni date, ni
 * note, ni tags, ni commentaires. `kind` est nécessaire pour choisir `<img>` ou
 * `<video>` ; `width`/`height` (nullables comme en base) pour la grille justifiée.
 *
 * `id` reste un uuid opaque : il ne sert qu'à re-désigner le média DANS ce partage, et
 * chaque requête média revalide cette appartenance.
 */
export const publicShareAssetSchema = z.object({
    id: z.uuid(),
    kind: assetKindSchema,
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
});
/**
 * En-tête d'un partage résolu (`GET /api/shares/:token`) : de quoi peindre le titre et
 * décider si le bouton « Télécharger » a lieu d'être. Ne porte ni l'identité du
 * créateur, ni le nom du dossier, ni l'échéance — un visiteur n'a pas à savoir QUI
 * partage, NI d'où ça vient, NI jusqu'à quand.
 */
export const publicShareSchema = z.object({
    title: z.string(),
    /** Nombre de médias VISIBLES (après application des cinq contrôles de résolution). */
    total: z.number().int().nonnegative(),
    /** `true` = les fichiers d'origine sont servis et téléchargeables. */
    shareOriginals: z.boolean(),
});
/**
 * Page de médias d'un partage (`GET /api/shares/:token/assets`). Pagination par
 * curseur opaque, même forme que la galerie — un partage de dossier peut être gros.
 */
export const publicShareAssetPageSchema = z.object({
    items: z.array(publicShareAssetSchema),
    total: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
});
/** Query de pagination publique. Sans `.transform` (garde boot `z.toJSONSchema`). */
export const publicShareAssetQuerySchema = z.object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
// ───────────────────────────────────────────────────────────────────────────────────
// GOUVERNANCE — ce que manipule un compte connecté.
// ───────────────────────────────────────────────────────────────────────────────────
/**
 * Item de partage pour « Mes partages » et la console admin. Ne porte JAMAIS le jeton
 * brut ni son hash.
 *
 * `revokedAt` non nul = révoqué (conservé pour l'historique, inutilisable). `expiresAt`
 * nul = éternel. Le couple `viewCount`/`lastViewedAt` n'est pas décoratif : c'est ce
 * qui signale qu'un lien oublié depuis un an est toujours consulté.
 */
export const shareSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    kind: shareKindSchema,
    /** Préfixe de dossier partagé (`kind = 'folder'`), sinon `null`. `''` = racine. */
    folderPath: z.string().nullable(),
    /** Nombre de médias du périmètre, tel que le verrait un visiteur aujourd'hui. */
    assetCount: z.number().int().nonnegative(),
    shareOriginals: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    /** `null` = éternel. */
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    /** `null` = actif. */
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    lastViewedAt: z.string().datetime({ offset: true }).nullable(),
    viewCount: z.number().int().nonnegative(),
    /** Auteur — affiché par la console admin, redondant dans « Mes partages ». */
    createdById: z.uuid(),
    createdByEmail: z.string(),
});
/** Liste de partages (`GET /api/shares/mine`, `GET /api/admin/shares`). */
export const shareListSchema = z.object({
    shares: z.array(shareSchema),
});
/**
 * Corps de `POST /api/libraries/:id/shares`.
 *
 * Volontairement PLAT plutôt qu'union discriminée : `folderPath` et `assetIds` sont
 * tous deux optionnels ici, et le HANDLER exige l'un XOR l'autre selon `kind`. Un
 * schéma plat traverse `z.toJSONSchema` sans surprise, et le message d'erreur produit
 * par le handler est plus clair que celui d'une union en échec.
 *
 * `assetIds` est REJETÉ (et non rogné) s'il contient un asset hors du périmètre du
 * créateur : partager silencieusement moins que demandé est un mensonge.
 */
export const createShareSchema = z.object({
    title: z.string().trim().min(1).max(SHARE_TITLE_MAX),
    kind: shareKindSchema,
    /** Requis si `kind = 'folder'` ; `''` partage toute la bibliothèque. */
    folderPath: z.string().max(1024).optional(),
    /** Requis et non vide si `kind = 'selection'`. */
    assetIds: z.array(z.uuid()).min(1).max(SHARE_ASSET_IDS_MAX).optional(),
    expiresIn: shareExpirySchema,
    /** Défaut `true` : partager, c'est donner les fichiers (ADR-0009). */
    shareOriginals: z.boolean().default(true),
});
/**
 * Réponse de `POST /api/libraries/:id/shares` : l'item créé + le JETON BRUT.
 *
 * ⚠ `secret` n'est renvoyé QUE par cette réponse, UNE SEULE FOIS. Le client doit
 * afficher l'URL `/p/<secret>` immédiatement — elle est irrécupérable ensuite.
 */
export const shareCreatedSchema = z.object({
    share: shareSchema,
    /** Jeton brut (base64url), affiché une seule fois côté client. */
    secret: z.string(),
});
//# sourceMappingURL=share.js.map